import type { TokenResponseExtended as TokenResponse } from "owa-service/lib/tokenResponseExtended";
import { type ConsumerTokenRequestParams, isOpxResourceTokenHandlingEnabled, } from "owa-tokenprovider-utils";
import sleep from "owa-sleep";
import { getOrigin } from "owa-url/lib/getOrigin";
import { getResourceToken } from "./getResourceToken";
import { recordRetryAttempt, recordRetryFailure } from "./recordRetryTelemetry";
import { isOpxHostInitialized } from "owa-opx";
import type { TokenPerformanceLogger } from "./TokenPerformanceLogger";

const TELEMETRY_PREFIX = "FetchFromHostAppConsumer";

export async function fetchTokenFromHostAppForConsumerWithRetry(perfLog: TokenPerformanceLogger, retryCount: number, retryDelay: number, consumerParams: ConsumerTokenRequestParams, attemptIndex: number = 1, backoffAccumulatedMs: number = 0): Promise<TokenResponse> {
    if (retryCount < 1) {
        const error = new Error("Max retrying limit (1) reached");
        recordRetryFailure(perfLog, TELEMETRY_PREFIX, attemptIndex - 1, backoffAccumulatedMs);
        return Promise.reject(error);
    }
    
    return fetchTokenFromHostAppForConsumer(perfLog, consumerParams).then(result => {
        recordRetryAttempt(perfLog, TELEMETRY_PREFIX, attemptIndex, backoffAccumulatedMs);
        return result;
    }, error => {
        // Retry only if attempts remain. On the final attempt, reject with the real error.
        if (retryCount > 1) {
            const sleepStart = Date.now();
            return sleep(retryDelay).then(() => fetchTokenFromHostAppForConsumerWithRetry(perfLog, retryCount - 1, retryDelay * 2, { ...consumerParams }, attemptIndex + 1, backoffAccumulatedMs + Math.max(0, Date.now() - sleepStart)));
        }
        
        recordRetryFailure(perfLog, TELEMETRY_PREFIX, attemptIndex, backoffAccumulatedMs);
        return Promise.reject(error);
    });
}

export async function fetchTokenFromHostAppForConsumer(perfLog: TokenPerformanceLogger, consumerParams: ConsumerTokenRequestParams): Promise<TokenResponse> {
    perfLog.addCheckpoint("TokenRetrievalFromHostApp_Start");
    const bridgeStart = Date.now();
    let token: TokenResponse | undefined;
    try {
        const resource = consumerParams.resource && isOpxHostInitialized() && isOpxResourceTokenHandlingEnabled()
            ? consumerParams.resource
            : getOrigin();
        token = await getResourceToken(resource, consumerParams.wwwAuthenticateHeader, consumerParams.mailboxInfo, consumerParams.scope, consumerParams.requestId, consumerParams.claims);
    }
    finally {
        perfLog.addCustomProperty("HostBridgeMs", Math.max(0, Date.now() - bridgeStart));
    }
    
    if (token) {
        perfLog.addCheckpoint("TokenRetrievalFromHostApp_End");
        
        if (token?.AccessToken) {
            perfLog.addCustomProperty("HostBridgeStatus", "success");
        }
        return token;
    }
    return {};
}
